import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Icon,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../../../constants/obNextGenConstants';
import testIds from '../../../../constants/testIds';
import useLocalization from '../../../../hooks/use-localize';
import { type RootState } from '../../../../redux/obNextGenStore';
import FileUploadModal from '../../../DiscoveryFileUpload/components/FileUploadModal/FileUploadModal';
import { resetDiscoveryFileUploadState } from '../../../DiscoveryFileUpload/data-store/DiscoveryFileUploadSlice';
import {
  type ConnectionPayload,
  type FormInputFields,
} from '../../data-layer/ApplicationCard';
import {
  type DiscoveryExistingConnectionState,
  resetDiscoveryExistingConnectionState,
} from '../../data-layer/DiscoveryExistingConnectionSlice';
import {
  type DiscoverySaveConnectionState,
  resetDiscoverySaveConnectionState,
} from '../../data-layer/DiscoverySaveConnectionSlice';
import { resetExistingConnectionDetailsState } from '../../data-layer/ExistingConnectionSlice';
import ApplicationSuccess from '../application-success/ApplicationSuccess';
import ApplicationCardContent from '../ApplicationCardContent/ApplicationCardContent';
import ApplicationCardFooter from '../ApplicationCardFooter/ApplicationCardFooter';
import ExistingConnectionFooter from '../existing-connection-footer/ExistingConnectionFooter';

import './ApplicationCard.css';

type ValueOf<T> = T[keyof T];

interface ApplicationCardProps {
  title: string;
  description: string;
  applicationGroup: string;
  name: ValueOf<typeof ApplicationTypes> | string;
  image: string;
  connectionType: string;
  appTypeKey: number;
  status: string;
}

enum ApplicationTypes {
  SERVICENOW = 'ServiceNow',
  IMPORTFILE = 'FileUpload',
}

/**
 * Application Card Component
 */
function ApplicationCard({
  title,
  description,
  applicationGroup,
  name,
  image,
  connectionType,
  appTypeKey,
  status,
}: ApplicationCardProps) {
  const dispatch = useDispatch();
  const formatMessage = useLocalization();

  const [useExisting, setUseExisting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);
  const [connectionJsonString, setConnectionString] = useState('');
  const [connectionFormFields, setConnectionFormFields] = useState<
    FormInputFields[]
  >([]);
  const [createNewConnection, setCreateNewConnection] = useState(false);
  const [hideContentFooter, setHideContentFooter] = useState(false);
  const [showExistingConnectionFooter, setShowExistingConnectionFooter] =
    useState(false);
  const [existingConnectionPayload, setExistingConnectionPayload] = useState<
    ConnectionPayload | undefined
  >();
  const { GET_APPLICATION_ICONS } = useMemo(getObNextGenApiUrlLists, []);

  const discoverySaveStatus = useSelector(
    (state: RootState): DiscoverySaveConnectionState =>
      state.discoverySaveConnection as DiscoverySaveConnectionState
  );

  const discoveryExisitngConnectionSaveStatus = useSelector(
    (state: RootState): DiscoveryExistingConnectionState =>
      state.discoveryExistingConnection as DiscoveryExistingConnectionState
  );

  const isImportFile = name === ApplicationTypes.IMPORTFILE;

  const handleModalOpen = () =>
    isImportFile ? setIsFileUploadModalOpen(true) : setIsModalOpen(true);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsFileUploadModalOpen(false);

    setTimeout(() => {
      setUseExisting(false);
      handleBackToExistingConnection(false);
      dispatch(resetDiscoveryExistingConnectionState());
      dispatch(resetDiscoverySaveConnectionState());
      dispatch(resetDiscoveryFileUploadState());
    }, 300);
  };

  const modalTitleId = `modal-title-${name}`;
  const modalDescId = `modal-desc-${name}`;

  const handleContentBody = (): React.ReactElement => {
    if (
      discoverySaveStatus.data != null &&
      discoverySaveStatus.data.iobAppKey !== undefined
    ) {
      return (
        <ApplicationSuccess
          resetUseExisting={setUseExisting}
          useExisting={useExisting}
          openAsModal={false}
          sourceAppId={78}
          skip={handleModalClose}
          appTypeKey={appTypeKey}
          image={image}
          iobAppKey={discoverySaveStatus.data.iobAppKey}
        />
      );
    }

    if (
      discoveryExisitngConnectionSaveStatus.data != null &&
      discoveryExisitngConnectionSaveStatus.data.iobAppId !== undefined
    ) {
      return (
        <ApplicationSuccess
          resetUseExisting={setUseExisting}
          useExisting={useExisting}
          openAsModal={false}
          sourceAppId={78}
          skip={handleModalClose}
          appTypeKey={appTypeKey}
          image={image}
          iobAppKey={discoveryExisitngConnectionSaveStatus.data.iobAppId}
        />
      );
    }

    return (
      <ApplicationCardContent
        getAllFormInputValue={setConnectionFormFields}
        applicationType={title}
        connectionType={connectionType}
        appTypeKey={appTypeKey}
        image={image}
        connectionJsonString={setConnectionString}
        disableContentFooter={setHideContentFooter}
        createNewConnection={createNewConnection}
        showExistingConnectionFooter={setShowExistingConnectionFooter}
        existingPayload={setExistingConnectionPayload}
      />
    );
  };

  const handleCreateNewConnection = () => {
    setCreateNewConnection(true);
    dispatch(resetExistingConnectionDetailsState());
    dispatch(resetDiscoverySaveConnectionState());
  };

  const handleBackToExistingConnection = (value: boolean) => {
    setCreateNewConnection(value);
    dispatch(resetExistingConnectionDetailsState());
    dispatch(resetDiscoverySaveConnectionState());
  };

  const handleContentFooter = (): React.ReactElement => {
    if (
      discoverySaveStatus.data !== null ||
      hideContentFooter ||
      discoveryExisitngConnectionSaveStatus.data !== null
    ) {
      return <Box />;
    }

    if (showExistingConnectionFooter && !createNewConnection) {
      return (
        <ExistingConnectionFooter
          usingExistingConnection={setUseExisting}
          handleCreateNewConnection={handleCreateNewConnection}
          existingConnectionPayload={existingConnectionPayload}
        />
      );
    }

    return (
      <ApplicationCardFooter
        appTypeKey={appTypeKey.toString()}
        connectionType={connectionType}
        connectionJson={connectionJsonString}
        allFields={connectionFormFields}
        createNewConnection={createNewConnection}
        backToExistingConnection={handleBackToExistingConnection}
      />
    );
  };

  return (
    <section
      className='ApplicationCard-Container'
      aria-labelledby={`card-title-${name}`}>
      <span className='ApplicationCard-Container-Icon' aria-hidden='true'>
        <img
          className='ApplicationCard-Container-Icon-Image'
          src={`${getBaseUrlObNextGen()}${GET_APPLICATION_ICONS}/${image}`}
          alt='Application icon'
        />
      </span>

      <header className='ApplicationCard-Container-Header'>
        <Typography kind='h3' desktop='h3-d' mobile='h3-d'>
          {title}
        </Typography>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color='--color-foreground-neutral-subtle'>
          {applicationGroup}
        </Typography>
      </header>

      <section
        className='ApplicationCard-Container-Description'
        aria-labelledby={modalDescId}>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color='--color-foreground-neutral-subtle'>
          {formatMessage(description, 'Application Description')}
        </Typography>
      </section>

      <span className='ApplicationCard-Container-Button'>
        <Button
          dataTestId={testIds.DISCOVERY_APPLICATIONCARD_SELECT_BUTTON}
          aria-label={`Select ${title}`}
          type='button'
          kind='subtle'
          size='large'
          isDisabled={status === 'disable'}
          onClick={handleModalOpen}>
          {formatMessage('INTEGRATIONS.DISCOVERY.SELECT', 'Select')}
        </Button>
      </span>

      <ModalIsland
        headerClassName='ApplicationCard-Container-Modal-Header'
        bodyClassName={
          discoverySaveStatus.data !== null
            ? 'ApplicationCard-Container-Modal-BodySuccess'
            : 'ApplicationCard-Container-Modal-Body'
        }
        kind='default'
        onClose={handleModalClose}
        isOpen={isModalOpen}
        title={formatMessage('INTEGRATIONS.MODAL_TITLE.DISCOVERY', 'Discovery')}
        className='OnboardingModal'
        aria-labelledby={modalTitleId}
        aria-describedby={modalDescId}
        headerIcon={
          <Icon kind='ManageOverview' size='large' aria-hidden='true' />
        }
        contentBody={handleContentBody()}
        FooterActionBarComp={handleContentFooter()}
      />

      <FileUploadModal
        isModalOpen={isFileUploadModalOpen}
        name={name}
        fileUploadSourceAppId={appTypeKey}
        fileUploadIobAppId={null}
        handleCloseModal={handleModalClose}
      />
    </section>
  );
}

export default ApplicationCard;
